import { Box, Button, styled, Typography } from "@mui/material";
import NoFiltersMatchImage from "assets/illustrations/noMatchFiltersImage.svg";
import DishTypeItem from "components/dishTypeItem";
import { SaleWrapper } from "components/dishTypeItem/components/SaleWrapper";
import { TutorialModalContent } from "components/drawerActionBar/components/TutorialModal/TutorialModalContent";
import { Header } from "components/global/components/Header";
import { BasicModal } from "components/global/modals/BasicModal";
import { useFilters } from "hooks/useFilters";
import { useFridgeLights } from "hooks/useFridgeLights";
import useMealListEvents from "hooks/useMealListEvents";
import { useAppDispatch, useAppSelector } from "hooks/utilsHooks/storeHooks";
import { type RefObject, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
	resetAppState,
	selectAppState,
	selectHowToPanelVisibility,
	selectOpenedDetailDishTypeId,
	setHowToBannerVisibility,
} from "slices/appStateSlice";
import {
	selectAvailableDishTypes,
	selectAvailableDishTypesIds,
	selectDishTypes,
	selectDishTypesNotIncludedInFilters,
} from "slices/dishesSlice";
import { selectOrder } from "slices/transactionSlice";
import { LightingModeType, TransactionState } from "types";
import { AppState, DishItemDisplayType } from "types/mainTypes";
import { getSpotlightDishTypeId } from "utils/lightingUtils";
import { scrollIntoView } from "utils/scrollIntoView";
import { DownloadAppBanner } from "../howToBanner/DownloadAppBanner";

/**
 * Represents a component that displays a list of meal types.
 */
export const MealList = () => {
	const openDetailDishTypeId = useAppSelector(selectOpenedDetailDishTypeId);
	const dispatch = useAppDispatch();
	const appState = useAppSelector(selectAppState);
	const isHowToBannerVisible = useAppSelector(selectHowToPanelVisibility);
	const order = useAppSelector(selectOrder);
	const availableDishTypeIds = useAppSelector(selectAvailableDishTypesIds);
	const fridgeDishes = useAppSelector(selectDishTypes);
	const availableDishTypes = useAppSelector(selectAvailableDishTypes);
	const unavailableDishTypes = useAppSelector(selectDishTypesNotIncludedInFilters);

	const { allergens, resetAllergens } = useFilters();
	const { toggleLight, dishTypeLights, toggleFridgeLightingMode, highlightSingleDishType, lights } = useFridgeLights();
	// Memoised scalar derived from per-dish lighting state. Used by effect 2 to detect
	// BE-driven spotlight clears (e.g. the StockCheck transition repaints per-dish to
	// all-Default) and re-assert the spotlight on `openDetailDishTypeId`. Stays stable
	// across subscription echoes that don't actually change the spotlight target — that
	// stability + the equality guard inside effect 2 is what prevents the original
	// subscription-echo flicker loop.
	const currentSpotlightDishTypeId = useMemo(
		() => (dishTypeLights ? getSpotlightDishTypeId(dishTypeLights) : null),
		[dishTypeLights],
	);
	const howToBannerRef = useRef<HTMLDivElement>(null);
	const { handleLightsStateWhenFilterIsActive, handleOpen, openDishTypeDetail } = useMealListEvents(
		availableDishTypeIds,
		fridgeDishes,
	);

	const { t } = useTranslation();

	const hasOrder = order != null;
	const orderState = order?.state;

	// Latest-refs used by guards inside the effects below. Deliberately kept out of effect deps:
	//  - availableDishTypeIdsRef: selectAvailableDishTypesIds returns a fresh Set on every
	//    setDishTypes dispatch (FRIDGE_SUBSCRIPTION push), which would otherwise re-fire the
	//    effect and create a TOGGLE_SPOTLIGHT_MODE_MUTATION echo loop.
	//  - lightsRef: lets guards short-circuit redundant lighting mutations without making
	//    `lights` an effect dependency. Updated from both the initial query and from
	//    FRIDGE_SUBSCRIPTION pushes (see DeviceContext.onData).
	//  - allergensRef: lets handleSingleDishTypeAction read the latest allergens without
	//    subscribing to allergen changes — handleFridgeLightingForNonIdleState owns that
	//    trigger. Eliminates the previous double-fire of handleLightsStateWhenFilterIsActive
	//    on pure allergen changes.
	const availableDishTypeIdsRef = useRef(availableDishTypeIds);
	availableDishTypeIdsRef.current = availableDishTypeIds;
	const lightsRef = useRef(lights);
	lightsRef.current = lights;
	const allergensRef = useRef(allergens);
	allergensRef.current = allergens;
	// Previous-value ref, NOT a latest-ref: handleSingleDishTypeAction reads it at the top of
	// each effect run (BEFORE branches fire) and overwrites it with the current value
	// immediately after — so "previous" here means "value at the start of the current effect
	// run", not "value at the end of the prior run". This lets the effect detect the
	// non-null → null transition (detail closed) and explicitly clear the per-dish spotlight.
	// Overall fridge mode stays Active across the spotlight lifecycle, so the lightsRef-based
	// mode guard can't see this transition on its own. Initialised with the current value so a
	// fresh mount with no detail open does not look like a close (see test at "does not clear
	// the per-dish spotlight on mount when no detail was ever open").
	const previousOpenDetailDishTypeIdRef = useRef(openDetailDishTypeId);
	// Previous-value ref for orderState. At Finished, the BE sends a lighting reset
	// (lighting.mode=Active + every dishes.types[].lighting.mode=null) and that frame is
	// authoritative — handleSingleDishTypeAction must not dispatch any mutations against it.
	// The dismiss transition (Finished → null on the next commit, when finishOrderState clears
	// `order`) must also be suppressed: otherwise the close branch fires an all-Default for the
	// remaining available dishes, leaving the just-purchased (sold-out, no longer in
	// availableDishTypeIds) dish stuck at the BE's null state while the others light up — that's
	// the "spotlight returns with X dark" symptom from the production report.
	const previousOrderStateRef = useRef(orderState);

	// biome-ignore lint/correctness/useExhaustiveDependencies: intentionally mount-only — opens the initial dish type detail once
	useEffect(function setInitialOpenedDishType() {
		if (dishTypeLights) {
			const initialHighlightedDishTypeId = getSpotlightDishTypeId(dishTypeLights);
			if (initialHighlightedDishTypeId !== null && allergens.length === 0) {
				openDishTypeDetail(initialHighlightedDishTypeId);
			}
		}
	}, []);

	// Effect-1 → Effect-2 ordering is load-bearing: effect 1 writes `lightsRef.current = Active`
	// synchronously before its mutation, so the next effect in the same commit (effect 2) sees
	// the post-mutation value and the on-mount mode guard doesn't double-dispatch. React fires
	// effects in declaration order — DO NOT reorder these two without updating both guards.
	useEffect(
		function handleFridgeLightingForNonIdleState() {
			if (
				allergens.length > 0 &&
				orderState !== TransactionState.StockCheck &&
				orderState !== TransactionState.Finished
			) {
				handleLightsStateWhenFilterIsActive();
				return;
			}
			// Detail (if any) is already in the desired state. Do NOT call handleOpen here —
			// it has toggle semantics and was closing the currently-open detail every time
			// this effect ran.
			if (
				appState !== AppState.IDLE &&
				orderState !== TransactionState.Finished &&
				lightsRef.current !== LightingModeType.Active
			) {
				// Update the ref synchronously so a later effect in the same commit (e.g.
				// handleSingleDishTypeAction) doesn't re-fire the same transition before
				// the external `lights` prop has propagated back.
				lightsRef.current = LightingModeType.Active;
				toggleFridgeLightingMode(LightingModeType.Active);
			}
		},
		[allergens, appState, orderState, handleLightsStateWhenFilterIsActive, toggleFridgeLightingMode],
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: `lights` is a trigger-only dep (read indirectly via `lightsRef.current`, synced on every render) so the H-2 Active-recovery branch re-fires on subscription mode echoes — biome would otherwise flag it as unused
	useEffect(
		function handleSingleDishTypeAction() {
			const previousOpenDetailDishTypeId = previousOpenDetailDishTypeIdRef.current;
			previousOpenDetailDishTypeIdRef.current = openDetailDishTypeId;
			const previousOrderState = previousOrderStateRef.current;
			previousOrderStateRef.current = orderState;

			// BE owns lighting during and immediately after Finished. The Finished frame is an
			// explicit reset (mode=Active + per-dish null); the dismiss frame (orderState going
			// from Finished to null on finishOrderState) is the user closing the summary while
			// that reset is still authoritative. Returning here suppresses both the open-branch
			// re-assert and the close-branch all-Default that would otherwise fight the reset.
			if (orderState === TransactionState.Finished || previousOrderState === TransactionState.Finished) {
				return;
			}

			if (openDetailDishTypeId !== null && appState === AppState.ACTIVE) {
				// Re-assert Active mode if it drifted independently of us — e.g. a FRIDGE_SUBSCRIPTION
				// echo flipped `lighting.mode` to StandBy while a spotlight is up. Effect 1 doesn't
				// watch `lights` (adding it would duplicate the filter-active dispatch path), so
				// this branch is the recovery point. Same synchronous-ref pattern as effects 1/3.
				if (lightsRef.current !== LightingModeType.Active) {
					lightsRef.current = LightingModeType.Active;
					toggleFridgeLightingMode(LightingModeType.Active);
				}
				// Re-assert the per-dish spotlight only if it has actually diverged from the
				// open detail. The BE may clear per-dish to all-Default mid-order (notably at the
				// StockCheck transition — see commit message), wiping the spotlight while the
				// user is still inspecting the dish; the dep on `currentSpotlightDishTypeId`
				// re-fires this effect when that happens. The equality guard is also what makes
				// the echo of our own TOGGLE_SPOTLIGHT_MODE_MUTATION terminate the loop instead
				// of bouncing forever — when the BE echoes X=Spotlight back, currentSpotlight
				// matches openDetailDishTypeId and we skip.
				if (currentSpotlightDishTypeId !== openDetailDishTypeId) {
					highlightSingleDishType(toggleLight, availableDishTypeIdsRef.current, openDetailDishTypeId);
				}
				return;
			}
			// `allergens` is intentionally not a dependency — handleFridgeLightingForNonIdleState
			// reacts to allergen changes. This effect reads the latest value via ref so it can
			// still pick the correct branch when openDetailDishTypeId / appState transitions
			// happen while a filter is active.
			if (!openDetailDishTypeId && allergensRef.current.length > 0) {
				handleLightsStateWhenFilterIsActive();
				return;
			}
			if (!openDetailDishTypeId && appState === AppState.ACTIVE) {
				// Detail just closed — clear the per-dish spotlight that the open branch set.
				// The mode-mutation guard below short-circuits when mode is already Active (the
				// common case while a spotlight is up), so it can't be relied on to clear the
				// per-dish state via the backend's mode-change side-effect.
				if (previousOpenDetailDishTypeId !== null) {
					highlightSingleDishType(toggleLight, availableDishTypeIdsRef.current, null);
				}
				if (lightsRef.current !== LightingModeType.Active) {
					lightsRef.current = LightingModeType.Active;
					toggleFridgeLightingMode(LightingModeType.Active);
				}
			}
		},
		[
			openDetailDishTypeId,
			appState,
			// `orderState` — read directly in the Finished gate (and re-fires the effect on
			//   order/refill state transitions, where the BE may repaint per-dish defaults).
			// `lights` — trigger-only; read indirectly via `lightsRef.current`. Needed in deps
			//   so the H-2 Active-recovery branch sees subscription mode echoes.
			// `currentSpotlightDishTypeId` — read directly in the spotlight-divergence guard;
			//   re-fires when the BE clears per-dish (e.g. StockCheck). The equality guard in
			//   the open branch is what stops this dep from re-introducing the echo-driven
			//   flicker loop fixed in 6aa438e — the value is stable once the BE echoes the
			//   spotlight back.
			orderState,
			lights,
			currentSpotlightDishTypeId,
			handleLightsStateWhenFilterIsActive,
			highlightSingleDishType,
			toggleFridgeLightingMode,
			toggleLight,
		],
	);

	useEffect(
		function resetViewWhenIdle() {
			if (appState === AppState.IDLE && !hasOrder) {
				scrollIntoView(howToBannerRef);
				dispatch(resetAppState());
				if (lightsRef.current !== LightingModeType.StandBy) {
					lightsRef.current = LightingModeType.StandBy;
					toggleFridgeLightingMode(LightingModeType.StandBy);
				}
				return;
			}
			if (hasOrder && orderState === TransactionState.Finished) {
				scrollIntoView(howToBannerRef);
			}
		},
		[appState, hasOrder, orderState, dispatch, toggleFridgeLightingMode],
	);

	const isFridgeEmpty = useMemo(
		() => fridgeDishes && availableDishTypes.length === 0 && allergens.length > 0,
		[fridgeDishes, availableDishTypes, allergens],
	);

	return (
		<>
			<Header />
			{isFridgeEmpty && (
				<StyledEmptyFridgePageContent>
					<StyledEmptyFridgeHeadline variant="promoHeader">
						{t("dishListPage.filteredEmptyState")}
					</StyledEmptyFridgeHeadline>
					<NoFiltersMatchImage />
				</StyledEmptyFridgePageContent>
			)}
			<StyledDishTypeItemsWrapper data-testid="meal-list-dishes">
				<BasicModal
					toggleButton={
						<DownloadAppBanner
							ref={howToBannerRef}
							isVisible={isHowToBannerVisible}
							toggleBanner={() => dispatch(setHowToBannerVisibility(false))}
						/>
					}
					closable={true}
					modalContent={<TutorialModalContent />}
					fullHeight={true}
				/>

				{availableDishTypes.map((dishType) => (
					<SaleWrapper key={`mealListPage_item_${dishType.id}`} dishType={dishType}>
						<DishTypeItem
							dishType={dishType}
							type={DishItemDisplayType.LIST}
							isOpen={openDetailDishTypeId === dishType.id}
							onOpen={(ref: RefObject<HTMLDivElement | null>) => handleOpen(dishType.id, ref)}
						/>
					</SaleWrapper>
				))}
				{allergens.length > 0 && unavailableDishTypes.length > 0 && (
					<>
						<Box
							sx={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
							}}
						>
							<Typography color="white" variant="mealSub">
								{t("filters.dishesNotMatchingFilters")}
							</Typography>
							<Button
								onClick={() => {
									resetAllergens();
								}}
								variant="es-text"
							>
								{t("filters.resetButton")}
							</Button>
						</Box>
						{unavailableDishTypes.map((dishType) => (
							<SaleWrapper key={dishType.id} dishType={dishType}>
								<DishTypeItem
									dishType={dishType}
									type={DishItemDisplayType.LIST}
									isOpen={openDetailDishTypeId === dishType.id}
									onOpen={(ref: RefObject<HTMLDivElement | null>) => handleOpen(dishType.id, ref)}
									notFiltered={true}
								/>
							</SaleWrapper>
						))}
					</>
				)}
			</StyledDishTypeItemsWrapper>
		</>
	);
};

const StyledDishTypeItemsWrapper = styled(Box)(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	gap: theme.spacing(2),
	overflow: "auto",
	height: "calc(100vh - 350px)",
	paddingBottom: "200px",
}));

const StyledEmptyFridgePageContent = styled(Box)(({ theme }) => ({
	width: "100%",
	height: "100%",
	margin: "auto",
	display: "flex",
	flexDirection: "column",
	alignItems: "center",
	paddingTop: theme.spacing(2.5),
}));

const StyledEmptyFridgeHeadline = styled(Typography)(({ theme }) => ({
	textAlign: "center",
	color: theme.palette.copper.main,
	marginBottom: theme.spacing(3),
}));
